import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SignaturePad } from "@/components/SignaturePad";
import { PenLine, UserCheck } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "EMPLOYEE", l: "Employee" },
  { v: "SUBCONTRACTOR", l: "Subcontractor" },
  { v: "VISITOR", l: "Visitor" },
];

// mode: "self" (field worker signs themselves) | "kiosk" (deliverer signs the crew in)
export function SignOnDialog({ open, onOpenChange, talkId, expected, currentUser, mode, onSigned, topics = [], topicsRevision }) {
  const [who, setWho] = useState("");        // expected attendee id, or "__other__"
  const [name, setName] = useState("");
  const [type, setType] = useState("EMPLOYEE");
  const [company, setCompany] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "self") {
      setWho(currentUser?.id || "__other__");
      setName(currentUser?.name || "");
    } else {
      setWho("");
      setName("");
      setType("EMPLOYEE");
      setCompany("");
    }
  }, [open, mode, currentUser]);

  const unsigned = (expected || []).filter((e) => !e.signed);

  const pickWho = (v) => {
    setWho(v);
    if (v === "__other__") { setName(""); setType("EMPLOYEE"); }
    else {
      const e = (expected || []).find((x) => x.id === v);
      setName(e?.name || "");
      setType("EMPLOYEE");
    }
  };

  const onSave = async (dataUrl) => {
    if (!dataUrl) return toast.error("Please sign in the box first");
    if (!name.trim()) return toast.error("Enter a name");
    setSaving(true);
    try {
      const isPickedUser = who && who !== "__other__";
      const body = {
        topics_revision: topicsRevision,
        user_id: mode === "self" ? currentUser?.id : (isPickedUser ? who : null),
        worker_name: name.trim(),
        signature: dataUrl,
        attendance_type: mode === "self" ? "EMPLOYEE" : type,
        external_company: type === "EMPLOYEE" ? null : (company.trim() || null),
      };
      await api.post(`/tbx/talks/${talkId}/sign`, body);
      toast.success(`${name.trim()} signed on`);
      onOpenChange(false);
      onSigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sign-on failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto bg-white dark:bg-[#0D141C] border-zinc-200 dark:border-white/12" data-testid="signon-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase tracking-tight font-display text-2xl flex items-center gap-2">
            <PenLine className="w-6 h-6 text-[#FF5F15]" />
            {mode === "self" ? "Sign on" : "Crew sign-on"}
          </DialogTitle>
          <DialogDescription>
            {mode === "self"
              ? "Confirm you attended this toolbox talk with your signature."
              : "Pass the device around — each person confirms their name and signs."}
          </DialogDescription>
        </DialogHeader>

        <section className="border rounded p-3 text-sm"><h3 className="font-bold uppercase text-xs text-[#FF5F15]">{topics.length} topic{topics.length === 1 ? "" : "s"} covered</h3><ol className="list-decimal pl-5 mt-2 space-y-1">{topics.map((topic, i) => <li key={i}>{topic.title_snapshot}</li>)}</ol><p className="mt-2 text-xs text-zinc-500">One signature records attendance at this session containing all listed topics.</p></section>
        <div className="space-y-3">
          {mode === "kiosk" && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Who's signing?</Label>
                <Select value={who} onValueChange={pickWho}>
                  <SelectTrigger data-testid="signon-who"><SelectValue placeholder="Select person" /></SelectTrigger>
                  <SelectContent>
                    {unsigned.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    <SelectItem value="__other__">Someone else…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {who === "__other__" && (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold">Name</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="signon-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
                      <Select value={type} onValueChange={setType}>
                        <SelectTrigger data-testid="signon-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {type !== "EMPLOYEE" && (
                      <div>
                        <Label className="text-xs uppercase tracking-wider font-bold">Company</Label>
                        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company" data-testid="signon-company" />
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {mode === "self" && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-100 dark:bg-white/5">
              <UserCheck className="w-5 h-5 text-[#FF5F15]" />
              <span className="font-bold text-sm">{name}</span>
            </div>
          )}

          {(mode === "self" || who) && (name || who === "__other__") && (
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold mb-1.5 block">Signature</Label>
              <SignaturePad testIdPrefix="signon" onSave={onSave} />
              {saving && <div className="text-xs text-zinc-500 mt-1">Saving…</div>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
